import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { StorageService } from './storage.service';
import { NavItem } from '../layouts/full/vertical/sidebar/nav-item/nav-item';


interface User {
  profile?: string;
  locales?: any[];
  selectedCompany?: {
    nombre: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class NavDataService implements OnDestroy {
  private navItemsSource = new BehaviorSubject<NavItem[]>([]);
  public navItems$ = this.navItemsSource.asObservable();
  private storageSubscription: Subscription;

  constructor(private storage: StorageService) {
    this.storageSubscription = this.storage.user$.subscribe((user) => {
      if (user) {
        this.updateNavItems(user);
      }
    });
  }

  // Este método es el que movimos desde sidebar.component.ts
  private updateNavItems(user: User): void {
    const isSuperAdmin = user?.profile === 'superadmin';
    const profile = user?.profile;
    const isReadonly = profile === 'readonly';
    const isReporter = profile === 'reporter';
    
    // Lógica para identificar usuarios restringidos (Kaufmann)
    const isRestrictedUser = user?.profile !== 'admin' && 
                             user?.profile !== 'superadmin' && 
                             user?.profile !== 'bodega' &&
                             Array.isArray(user?.locales) && 
                             user.locales.length > 0;

    const isGrumanCompany = (companyName: string | undefined): boolean => {
      if (!companyName) return false;
      return companyName.toLowerCase().trim() === 'gruman';
    };

    const showMantenedores = isGrumanCompany(user?.selectedCompany?.nombre);
    const showSolicitarVisita = !showMantenedores;

    // Presupuestos NO se muestra a los de Solo Lectura
    const showPresupuestosCliente = !showMantenedores && !isRestrictedUser && !isReadonly;

    //  Reportes NO se muestra a los de Solo Lectura
    const showReportes = !isRestrictedUser && !isReadonly;

    const today = new Date();
    const formattedDate = today.toLocaleDateString('es-CL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });

    const isAdmin = user?.profile === 'admin' || user?.profile === 'superadmin' || user?.profile === 'bodega';
    
    const newNavItems: NavItem[] = [
      // Envolvemos el menú Admin para que solo lo vean los administradores
      ...(isAdmin ? [
        {
          navCap: 'Admin',
          children: [
            {
              displayName: 'Users',
              iconName: 'users',
              route: 'admin/users',
            },
          ],
        }
      ] : []),
      {
        navCap: 'Home',
      },
      {
        displayName: 'Escritorio',
        iconName: 'home',
        route: 'dashboards',
        bgcolor: 'primary',
      },
      ...(showMantenedores
        ? [
          {
            displayName: 'Programación',
            iconName: 'calendar',
            children: [
              {
                displayName: 'Generar programación',
                iconName: 'plus',
                bgcolor: 'primary',
                route: 'generar-programacion',
              },
            ],
          },
        ]
        : []),
      ...(showSolicitarVisita
        ? [
          // Ocultamos el botón de crear ticket si es Reporter
          ...(!isReporter ? [{
            displayName: 'Solicitar Visita',
            iconName: 'calendar-check',
            route: 'transacciones/solicitar-visita',
            bgcolor: 'primary',
          }] : []),
          {
            displayName: `Servicios del día ${formattedDate}`,
            iconName: 'calendar',
            bgcolor: 'primary',
            route: 'transacciones/solicitudes-del-dia-cliente',
          },
          
          // Condicionamos el bloque completo de Reportes
          ...(showReportes ? [{
            displayName: 'Reportes',
            iconName: 'file-search',
            children: [
              {
                displayName: 'Reporte de activos',
                iconName: 'file-delta',
                route: 'reportes/reporte-de-activos',
              },
              {
                displayName: 'Reporte de repuestos por día',
                iconName: 'graph',
                route: 'reportes/repuestos-por-dia',
              },
              {
                displayName: 'Reporte de repuestos por mes',
                iconName: 'calendar',
                route: 'reportes/repuestos-por-mes',
              },
              {
                displayName: 'Reporte SLA',
                iconName: 'shield-check',
                route: 'reportes/reporte-sla',
              },
              {
                displayName: 'Reporte de Causa Raíz',
                iconName: 'chart-bar', 
                route: 'reportes/reporte-causa-raiz',
              },
              {
                displayName: 'Reporte de Locales',
                iconName: 'building-community',
                route: 'reportes/reporte-locales',
              },
              {
                displayName: 'Trazabilidad de Equipos',
                iconName: 'history',
                route: 'reportes/reporte-trazabilidad',
              },
              {
                displayName: 'Cumplimiento Preventivo',
                iconName: 'calendar-stats',
                route: 'reportes/cumplimiento-preventivo',
              },
              {
                displayName: 'Gastos Acumulados',
                iconName: 'receipt-2',
                route: 'reportes/gastos-acumulados',
              },
              {
                displayName: 'Técnicos por Repuestos',
                iconName: 'user-cog',
                route: 'reportes/tecnicos-por-repuestos',
              },
              {
                displayName: 'Técnicos por Servicio',
                iconName: 'users-group',
                route: 'reportes/tecnicos-por-servicio',
              },
              {
                displayName: 'Cumplimiento Diario',
                iconName: 'chart-line',
                route: 'reportes/cumplimiento-diario',
              },
              {
                displayName: 'Programados No Ejecutados',
                iconName: 'calendar-off',
                route: 'reportes/no-ejecutados',
              },
              {
                displayName: 'Tiempo por Servicio',
                iconName: 'clock-hour-4',
                route: 'reportes/tiempo-por-servicio',
              },
              {
                displayName: 'HH por Técnico',
                iconName: 'clock-hour-8',
                route: 'reportes/hh-por-tecnico',
              },
              {
                displayName: 'Reporte de Odómetro',
                iconName: 'steering-wheel',
                route: 'reportes/odometro',
              },
              {
                displayName: 'Estado de Pagos',
                iconName: 'receipt-2',
                bgcolor: 'primary',
                route: '/reportes/reporte-estado-pagos',
              },
              {
                displayName: 'Histórico Programación',
                iconName: 'calendar-time',
                route: '/reportes/historico-programacion',
              },
            ],
          }] : []),
        ]
        : []),

      {
        displayName: 'Solicitudes',
        iconName: 'clipboard-list',
        children: [
          {
            displayName: 'Visita Pendiente',
            iconName: 'home-shield',
            route: 'transacciones/solicitudes-de-visita/pendientes',

          },
          {
            displayName: 'Visita aprobadas',
            iconName: 'checkbox',
            route: 'transacciones/solicitudes-de-visita/aprobadas',
          },
          {
            displayName: 'Visita rechazadas',
            iconName: 'ban',
            route: 'transacciones/solicitudes-de-visita/rechazadas',
          },
          {
            displayName: 'Visitas programadas',
            iconName: 'calendar-event', 
            route: 'transacciones/solicitudes-de-visita/programadas',
          },
          ...(showMantenedores
            ? [
              {
                displayName: 'Visita finalizadas',
                iconName: 'flag',
                route: 'transacciones/solicitudes-de-visita/finalizadas',
              },
            ]
            : []),
          {
            displayName: 'Visita validadas',
            iconName: 'user-check',
            route: 'transacciones/solicitudes-de-visita/validadas',
          },
          {
            displayName: 'Visitas atendidas en proceso',
            iconName: 'loader',
            route: 'transacciones/solicitudes-de-visita/atendidas-en-proceso'
          }
        ],
      },
      ...(showMantenedores
        ? [
          {
            displayName: 'Servicios',
            iconName: 'server',
            route: 'mantenedores',
            children: [
              {
                displayName: 'Servicios realizados ',
                iconName: 'phone-check',
                route: 'transacciones/servicios-realizados',
              },
              {
                displayName: `Servicios del día | ${formattedDate}`,
                iconName: 'calendar',
                route: 'transacciones/solicitudes-del-dia',
              },
            ]
          },
          {
            displayName: 'Reportes',
            iconName: 'file-search',
            children: [
              {
                displayName: 'Reporte de activos',
                iconName: 'file-delta',
                route: 'reportes/reporte-de-activos',
              },
              {
                displayName: 'Reporte de repuestos por día',
                iconName: 'graph',
                route: 'reportes/repuestos-por-dia',
              },
              {
                displayName: 'Reporte de repuestos por mes',
                iconName: 'calendar',
                route: 'reportes/repuestos-por-mes',
              },
              ...(!showMantenedores
                ? [
                  {
                    displayName: 'Informe de consumo',
                    iconName: 'graph',
                    route: 'reportes/informe-de-consumo',
                  },
                ]
                : []),
              {
                displayName: 'Reporte SLA',
                iconName: 'shield-check',
                route: 'reportes/reporte-sla',
              },
              {
                displayName: 'Reporte de Causa Raíz',
                iconName: 'chart-bar',
                route: 'reportes/reporte-causa-raiz',
              },
              {
                displayName: 'Reporte de Locales',
                iconName: 'building-community',
                route: 'reportes/reporte-locales',
              },
              {
                displayName: 'Trazabilidad de Equipos',
                iconName: 'history',
                route: 'reportes/reporte-trazabilidad',
              },
              {
                displayName: 'Cumplimiento Preventivo',
                iconName: 'calendar-stats',
                route: 'reportes/cumplimiento-preventivo',
              },
              {
                displayName: 'Gastos Acumulados',
                iconName: 'receipt-2',
                route: 'reportes/gastos-acumulados',
              },
              {
                displayName: 'Técnicos por Repuestos',
                iconName: 'user-cog',
                route: 'reportes/tecnicos-por-repuestos',
              },
              {
                displayName: 'Técnicos por Servicio',
                iconName: 'users-group',
                route: 'reportes/tecnicos-por-servicio',
              },
              {
                displayName: 'Cumplimiento Diario',
                iconName: 'chart-line',
                route: 'reportes/cumplimiento-diario',
              },
              {
                displayName: 'Programados No Ejecutados',
                iconName: 'calendar-off',
                route: 'reportes/no-ejecutados',
              },
              {
                displayName: 'Tiempo por Servicio',
                iconName: 'clock-hour-4',
                route: 'reportes/tiempo-por-servicio',
              },
              {
                displayName: 'HH por Técnico',
                iconName: 'clock-hour-8',
                route: 'reportes/hh-por-tecnico',
              },
              {
                displayName: 'Reporte de Odómetro',
                iconName: 'steering-wheel',
                route: 'reportes/odometro',
              },
              {
                displayName: 'Estado de Pagos',
                iconName: 'receipt-2',
                bgcolor: 'primary',
                route: '/reportes/reporte-estado-pagos',
              },
              {
                displayName: 'Histórico Programación',
                iconName: 'calendar-time',
                route: '/reportes/historico-programacion',
              },
            ],
          },
          {
            displayName: 'Mantenedores',
            iconName: 'user-cog',
            route: 'mantenedores',
            children: [
              
              // 🔥 Ocultamos Carga Masiva si no es Super Admin
              ...(isSuperAdmin ? [{
                displayName: 'Carga masiva',
                iconName: 'upload',
                route: 'transacciones/solicitudes-de-visita/carga-masiva',
              }] : []),

              {
                displayName: 'Carga masiva activos',
                iconName: 'calendar-plus', // Ícono sugerido (puedes usar 'file-upload' o similar)
                route: 'mantenedores/programacion-activos',
              },
              {
                displayName: 'Locales',
                iconName: 'building-store',
                route: 'mantenedores/locales',
              },
              {
                displayName: 'Móviles',
                iconName: 'car',
                route: 'mantenedores/vehiculos',
              },
              {
                displayName: 'Técnicos Gruman',
                iconName: 'users',
                route: 'mantenedores/tecnicos-gruman',
              },
              {
                displayName: 'Tipo activo',
                iconName: 'package',
                route: 'mantenedores/tipo-activo',
              },
              {
                displayName: 'Usuarios del sistema',
                iconName: 'users-group',
                route: 'mantenedores/usuarios',
              },
              {
                displayName: 'Técnicos móviles',
                iconName: 'user-bolt',
                route: 'mantenedores/tecnicos-moviles',
              },
              {
                displayName: 'Repuestos',
                iconName: 'tool',
                route: 'mantenedores/repuestos',
              },

              // 🔥 Ocultamos Clientes si no es Super Admin
              ...(isSuperAdmin ? [{
                displayName: 'Clientes',
                iconName: 'briefcase',
                route: 'mantenedores/clientes',
              }] : []),

              {
                displayName: 'Inspecciones',
                iconName: 'scan',
                route: 'mantenedores/lista-de-inspecciones',
              },

              {
                displayName: 'Listado de documentos',
                iconName: 'file-description',
                route: 'mantenedores/documentos',
              },
              {
                displayName: 'Tipo documento',
                iconName: 'files',
                route: 'mantenedores/documentos/tipo-documento',
              },
              {
                displayName: 'Tipo Servicio',
                iconName: 'server-2',
                route: 'mantenedores/tipo-servicio',
              },
              {
                displayName: 'Tipo Solicitud',
                iconName: 'clock',
                route: 'mantenedores/tipo-solicitud',
              },
              {
                displayName: 'Sectores de trabajo',
                iconName: 'map-pin',
                route: 'mantenedores/sectores-trabajo',
              },
              // {
              //   displayName: "Servicios",
              //   iconName: "home-shield",
              //   route: "mantenedores/servicios",
              // },
              {
                displayName: 'Causas raíz',
                iconName: 'file-search',
                route: 'mantenedores/causa-raiz',
              },

              {
                displayName: 'Activo fijo local',
                iconName: 'building-warehouse',
                route: 'mantenedores/activo-fijo-local',
              },
              {
                displayName: 'Especialidades',
                iconName: 'propeller',
                route: 'mantenedores/especialidades',
              },
              {
                displayName: 'Mes de facturación',
                iconName: 'receipt-2',
                route: 'mantenedores/mes-de-facturacion',
              },
            ],
          },
        ]
        : []),
      ...(showMantenedores
        ? [
          {
            displayName: 'Administración',
            iconName: 'settings',
            route: 'administracion',
            children: [
              {
                displayName: 'Estado de Pagos',
                iconName: 'receipt-2',
                bgcolor: 'primary',
                route: '/administracion/estado-pagos',
              },
              {
                displayName: 'Cierre de Mes',
                iconName: 'lock',
                bgcolor: 'warn',
                route: '/administracion/cierre-mes',
              },
              {
                displayName: "Presupuestos",
                iconName: "cash",
                bgcolor: "primary",
                route: "mantenedores/presupuestos",
              },
            ],
          },
        ]
        : []),
      ...(showPresupuestosCliente
        ? [
          {
            displayName: "Presupuestos",
            iconName: "cash",
            bgcolor: "primary",
            route: "mantenedores/presupuestos",
          },
        ]
        : []),
    ];

    // Emitimos la nueva lista de items a todos los suscriptores
    this.navItemsSource.next(newNavItems);
  }

  ngOnDestroy(): void {
    if (this.storageSubscription) {
      this.storageSubscription.unsubscribe();
    }
  }

  public getCurrentNavItems(): NavItem[] {
    return this.navItemsSource.getValue();
  }
}